import {
    SupportFeedAttachmentDto,
    SupportFeedCommentDto,
    SupportFeedEntryDto
} from '@api/ChallengeClient';
import { DatePicker } from '@components/date-picker';
import { Spinner } from '@components/spinner';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Card, CardContent } from '@components/ui/card';
import { Checkbox } from '@components/ui/checkbox';
import { Label } from '@components/ui/label';
import { Separator } from '@components/ui/separator';
import { I18n } from '@core/i18n';
import { injectTSDI } from '@core/tsdi';
import { cn } from '@core/utils/cn';
import {
    ChevronDown,
    ChevronUp,
    Download,
    Image,
    Pin,
    PinOff
} from 'lucide-react';
import React from 'react';
import { CommentState } from './comment-state-reducer';
import {
    KNOWN_ATTACHMENT_LABELS,
    formatMetadataValue
} from './support-feed-helpers';
import { EntityType, useSupportFeedStore } from './support-feed-store';

interface SupportFeedPageProps {
    entityId: number;
    entityType: EntityType;
}

const Avatar = ({
    src,
    alt,
    className
}: {
    src?: string;
    alt: string;
    className?: string;
}) => (
    <div
        className={cn(
            'size-10 shrink-0 overflow-hidden rounded-full bg-muted',
            className
        )}
    >
        {src && <img src={src} alt={alt} className="size-full object-cover" />}
    </div>
);

export const SupportFeedPage: React.FC<SupportFeedPageProps> = ({
    entityId,
    entityType
}) => {
    const { formatDateTime } = injectTSDI(I18n);
    const {
        entries,
        loading,
        loadingMore,
        error,
        hasMore,
        draftFrom,
        draftTo,
        setDraftFrom,
        setDraftTo,
        handleApplyFilters,
        handleClearFilters,
        selectedIds,
        selectedEntries,
        loadedEntryCount,
        selectedLoadedEntryCount,
        allLoadedEntriesSelected,
        someLoadedEntriesSelected,
        handleSelectionChange,
        handleSelectLoadedEntries,
        handleClearSelection,
        commentStateById,
        handleToggleComments,
        handleCommentLoadMore,
        pinUpdatingId,
        handleTogglePinned,
        handleLoadMore,
        handleExport
    } = useSupportFeedStore(entityId, entityType);
    const loadedSelectionLabel = `Select loaded (${selectedLoadedEntryCount}/${loadedEntryCount})`;
    const selectLoadedId = React.useId();

    return (
        <div>
            <div className="flex flex-wrap items-end justify-between gap-4 pb-4">
                <div className="flex flex-wrap items-end gap-4">
                    <DatePicker
                        label="From"
                        value={draftFrom}
                        onChange={(value) => void setDraftFrom(value)}
                        clearable
                        className="w-44"
                    />
                    <DatePicker
                        label="To"
                        value={draftTo}
                        onChange={(value) => void setDraftTo(value)}
                        clearable
                        className="w-44"
                    />
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={handleApplyFilters}
                    >
                        Apply
                    </Button>
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleClearFilters}
                    >
                        Clear
                    </Button>
                </div>
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id={selectLoadedId}
                            checked={
                                allLoadedEntriesSelected
                                    ? true
                                    : someLoadedEntriesSelected
                                      ? 'indeterminate'
                                      : false
                            }
                            disabled={loading || loadedEntryCount === 0}
                            onCheckedChange={() => {
                                if (allLoadedEntriesSelected) {
                                    handleClearSelection();
                                    return;
                                }

                                handleSelectLoadedEntries();
                            }}
                        />
                        <Label htmlFor={selectLoadedId}>
                            {loadedSelectionLabel}
                        </Label>
                    </div>
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={selectedEntries.length === 0}
                        onClick={handleExport}
                    >
                        <Download />
                        Export Selected ({selectedEntries.length})
                    </Button>
                </div>
            </div>

            {error && (
                <Alert variant="destructive" className="mb-4">
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}

            {loading ? (
                <div className="flex justify-center py-12">
                    <Spinner size="lg" />
                </div>
            ) : entries.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                    No feed entries found for the selected range.
                </p>
            ) : (
                <div className="flex flex-col gap-4">
                    {entries.map((entry) => (
                        <FeedEntryCard
                            key={entry.id}
                            entry={entry}
                            commentState={commentStateById[entry.id]}
                            selected={!!selectedIds[entry.id]}
                            pinUpdating={pinUpdatingId === entry.id}
                            formatDateTime={formatDateTime}
                            onToggleSelected={() =>
                                handleSelectionChange(entry.id)
                            }
                            onToggleComments={() =>
                                void handleToggleComments(entry)
                            }
                            onCommentLoadMore={() =>
                                void handleCommentLoadMore(entry.id)
                            }
                            onTogglePinned={() =>
                                void handleTogglePinned(entry)
                            }
                        />
                    ))}

                    {hasMore && (
                        <div className="flex justify-center">
                            <Button
                                variant="outline"
                                disabled={loadingMore}
                                onClick={() => void handleLoadMore()}
                            >
                                {loadingMore ? 'Loading...' : 'Load more'}
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const FeedEntryCard: React.FC<{
    entry: SupportFeedEntryDto;
    commentState: CommentState | undefined;
    selected: boolean;
    pinUpdating: boolean;
    formatDateTime(date?: string): string;
    onToggleSelected: () => void;
    onToggleComments: () => void;
    onCommentLoadMore: () => void;
    onTogglePinned: () => void;
}> = ({
    entry,
    commentState,
    selected,
    pinUpdating,
    formatDateTime,
    onToggleSelected,
    onToggleComments,
    onCommentLoadMore,
    onTogglePinned
}) => (
    <Card>
        <CardContent className="flex flex-col gap-4">
            <div
                className="flex cursor-pointer items-start justify-between gap-4"
                onClick={onToggleSelected}
            >
                <div className="flex items-center gap-4">
                    <Checkbox
                        checked={selected}
                        onClick={(e) => e.stopPropagation()}
                        onCheckedChange={() => onToggleSelected()}
                    />
                    <Avatar
                        src={entry.author?.image}
                        alt={entry.author?.name || 'User'}
                    />
                    <div>
                        <p className="font-semibold">
                            {entry.author?.name || 'Unknown User'}
                        </p>
                        <p className="text-sm text-muted-foreground">
                            {entry.author?.userId
                                ? `userId: ${entry.author.userId}`
                                : entry.author?.streamUserId || '-'}
                        </p>
                    </div>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2">
                    {entry.pinned && (
                        <Badge variant="info">
                            <Pin />
                            Pinned
                        </Badge>
                    )}
                    {entry.topicModerationApproved !== undefined && (
                        <Badge
                            variant={
                                entry.topicModerationApproved
                                    ? 'success'
                                    : 'warning'
                            }
                        >
                            {entry.topicModerationApproved
                                ? 'Moderation approved'
                                : 'Moderation rejected'}
                        </Badge>
                    )}
                    <span className="text-sm text-muted-foreground">
                        {formatDateTime(entry.createdDate)}
                    </span>
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={pinUpdating}
                        onClick={(e) => {
                            e.stopPropagation();
                            onTogglePinned();
                        }}
                    >
                        {entry.pinned ? <PinOff /> : <Pin />}
                        {entry.pinned ? 'Unpin' : 'Pin'}
                    </Button>
                </div>
            </div>

            {entry.text && <p className="whitespace-pre-wrap">{entry.text}</p>}

            {entry.mentionedUsers.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {entry.mentionedUsers.map((user) => (
                        <Badge
                            key={`${entry.id}-${user.streamUserId}`}
                            variant="outline"
                        >
                            Mention:{' '}
                            {user.name || user.userId || user.streamUserId}
                        </Badge>
                    ))}
                </div>
            )}

            {entry.attachments.length > 0 && (
                <div className="flex flex-col gap-2">
                    {entry.attachments.map((attachment, index) => (
                        <AttachmentCard
                            key={`${entry.id}-attachment-${index}`}
                            attachment={attachment}
                        />
                    ))}
                </div>
            )}

            <Separator />

            <div>
                <Button
                    size="sm"
                    variant="ghost"
                    disabled={(entry.commentCount || 0) === 0}
                    onClick={onToggleComments}
                >
                    {commentState?.expanded
                        ? 'Hide comments'
                        : `Expand comments (${entry.commentCount || 0})`}
                    {commentState?.expanded ? <ChevronUp /> : <ChevronDown />}
                </Button>

                {commentState?.expanded && (
                    <div className="mt-4">
                        {commentState.loading ? (
                            <div className="flex justify-center py-4">
                                <Spinner size="sm" />
                            </div>
                        ) : (
                            <div className="flex flex-col gap-3">
                                {commentState.items.map((comment) => (
                                    <CommentCard
                                        key={comment.id}
                                        comment={comment}
                                        formatDateTime={formatDateTime}
                                    />
                                ))}
                                {commentState.hasMore && (
                                    <div>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            disabled={commentState.loadingMore}
                                            onClick={onCommentLoadMore}
                                        >
                                            {commentState.loadingMore
                                                ? 'Loading comments...'
                                                : 'Load more comments'}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </CardContent>
    </Card>
);

const CommentCard: React.FC<{
    comment: SupportFeedCommentDto;
    formatDateTime(date?: string): string;
}> = ({ comment, formatDateTime }) => (
    <div className="flex flex-col gap-2 rounded-lg border bg-muted/50 p-4">
        <div className="flex items-center gap-3">
            <Avatar
                src={comment.author?.image}
                alt={comment.author?.name || 'User'}
                className="size-8"
            />
            <div>
                <p className="text-sm font-semibold">
                    {comment.author?.name || 'Unknown User'}
                </p>
                <span className="text-xs text-muted-foreground">
                    {comment.author?.userId
                        ? `userId: ${comment.author.userId}`
                        : comment.author?.streamUserId || '-'}
                </span>
            </div>
            <div className="flex-1" />
            <span className="text-xs text-muted-foreground">
                {formatDateTime(comment.createdDate)}
            </span>
        </div>

        {comment.text && (
            <p className="text-sm whitespace-pre-wrap">{comment.text}</p>
        )}

        {comment.attachments.length > 0 && (
            <div className="flex flex-col gap-2">
                {comment.attachments.map((attachment, index) => (
                    <AttachmentCard
                        key={`${comment.id}-attachment-${index}`}
                        attachment={attachment}
                        compact
                    />
                ))}
            </div>
        )}
    </div>
);

const AttachmentCard: React.FC<{
    attachment: SupportFeedAttachmentDto;
    compact?: boolean;
}> = ({ attachment, compact = false }) => {
    const metadata = Object.entries(attachment.custom || {}).slice(0, 5);
    const label = attachment.type
        ? KNOWN_ATTACHMENT_LABELS[attachment.type] || attachment.type
        : 'Attachment';
    const imageUrl = attachment.imageUrl || attachment.thumbUrl;
    const known = Boolean(KNOWN_ATTACHMENT_LABELS[attachment.type || '']);

    return (
        <div
            className={cn(
                'flex flex-col gap-2 rounded-lg border',
                compact ? 'p-3' : 'p-4'
            )}
        >
            <div className="flex flex-wrap items-center gap-2">
                <Badge variant={known ? 'info' : 'outline'}>
                    <Image />
                    {label}
                </Badge>
                {attachment.title && (
                    <span className="font-semibold">{attachment.title}</span>
                )}
            </div>

            {attachment.text && (
                <p className="text-sm text-muted-foreground">
                    {attachment.text}
                </p>
            )}

            {imageUrl && (
                <img
                    src={imageUrl}
                    alt={attachment.title || attachment.type || 'Attachment'}
                    className={cn(
                        'w-full rounded-lg object-cover',
                        compact ? 'max-h-[180px]' : 'max-h-[280px]'
                    )}
                />
            )}

            {!imageUrl &&
                (attachment.assetUrl ||
                    attachment.titleLink ||
                    attachment.ogScrapeUrl) && (
                    <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                        {attachment.assetUrl && (
                            <span>Asset: {attachment.assetUrl}</span>
                        )}
                        {attachment.titleLink && (
                            <span>Link: {attachment.titleLink}</span>
                        )}
                        {attachment.ogScrapeUrl && (
                            <span>Source: {attachment.ogScrapeUrl}</span>
                        )}
                    </div>
                )}

            {metadata.length > 0 && (
                <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                    {metadata.map(([key, value]) => (
                        <span key={`${attachment.type}-${key}`}>
                            {key}: {formatMetadataValue(value)}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};
